import { useState } from 'react';
import { X, FileText, Download, Trash2, Loader, Calendar, AlertTriangle, RefreshCw } from 'lucide-react';
import api from '../utils/api';

/**
 * 歷史報表彈窗元件
 * 
 * Props:
 * - isOpen: boolean - 是否顯示
 * - onClose: () => void - 關閉回調
 * - reports: array - 報表列表
 * - onSelectReport: (reportId) => void - 選擇報表時的回調（跳轉到明細分析）
 * - onClearReports: () => void - 清除所有報表
 * - onDeleteReport: (reportId) => void - 刪除單一報表後的回調（刷新資料）
 * - onRetryReport: (reportId) => Promise<void> - 重新分析失敗報表後的回調
 * - canEdit: boolean - 是否有編輯權限
 * - loading: boolean - 是否載入中
 */
export default function ReportHistoryModal({
  isOpen,
  onClose,
  reports = [],
  onSelectReport,
  onClearReports,
  onDeleteReport,
  onRetryReport,
  canEdit,
  loading,
}) {
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [expandedErrorId, setExpandedErrorId] = useState(null);
  const [retrying, setRetrying] = useState(null);

  if (!isOpen) return null;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '未知時間';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '未知時間';
      return date.toLocaleString('zh-TW', {
        timeZone: 'Asia/Taipei',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return '未知時間';
    }
  };

  const handleDownloadOriginal = async (e, report) => {
    e.stopPropagation();
    try {
      setDownloading(report.id);
      const response = await api.get(`/expense/${report.id}/download-original`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', report.file_name || `report-${report.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下載原始報表失敗:', error);
      alert('下載失敗，請稍後再試');
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteReport = async (e, report) => {
    e.stopPropagation();
    
    // 第一次點擊 → 顯示確認
    if (confirmDeleteId !== report.id) {
      setConfirmDeleteId(report.id);
      return;
    }
    
    // 第二次點擊 → 確認刪除
    try {
      setDeleting(report.id);
      setConfirmDeleteId(null);
      const res = await api.delete(`/expense/${report.id}`);
      
      if (res.data.refunded_amount) {
        alert(`✅ ${res.data.message}`);
      }
      
      // 通知父元件刷新
      if (onDeleteReport) {
        onDeleteReport(report.id);
      }
    } catch (error) {
      console.error('刪除報表失敗:', error);
      alert('刪除失敗: ' + (error.response?.data?.error || error.message));
    } finally {
      setDeleting(null);
    }
  };

  const getFailureDetail = (report) => {
    if (!report.error_message) return null;
    try {
      return JSON.parse(report.error_message);
    } catch {
      return { message: report.error_message };
    }
  };

  const handleRetryReport = async (e, report) => {
    e.stopPropagation();
    try {
      setRetrying(report.id);
      await api.post(`/expense/${report.id}/analyze`);
      if (onRetryReport) await onRetryReport(report.id);
    } catch (error) {
      console.error('重新分析報表失敗:', error);
      alert('重新分析失敗: ' + (error.response?.data?.error || error.message));
    } finally {
      setRetrying(null);
    }
  };

  // 點擊其他地方取消確認
  const handleCardClick = (report) => {
    if (confirmDeleteId) {
      setConfirmDeleteId(null);
      return;
    }
    onSelectReport(report.id);
  };

  // 按上傳時間倒序排列
  const sortedReports = [...reports].sort((a, b) => {
    const dateA = new Date(a.uploaded_at || a.created_at || 0);
    const dateB = new Date(b.uploaded_at || b.created_at || 0);
    return dateB - dateA;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 遮罩 */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 彈窗本體 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col animate-fade-in">
        {/* 標題列 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-500 rounded-lg">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">歷史報表</h2>
              <p className="text-xs text-gray-500">共 {reports.length} 份報表</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {canEdit && reports.length > 0 && (
              <button
                onClick={onClearReports}
                disabled={loading}
                className="text-sm px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-1"
                title="清除所有報表"
              >
                <Trash2 className="w-4 h-4" />
                <span>清除全部</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* 報表列表 */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {reports.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-16 h-16 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">尚無歷史報表</p>
              <p className="text-sm mt-1">上傳報表後會顯示在這裡</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedReports.map((report) => (
                <div
                  key={report.id}
                  onClick={() => handleCardClick(report)}
                  className={`group p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                    confirmDeleteId === report.id
                      ? 'bg-red-50 border-red-300'
                      : 'bg-gray-50 hover:bg-blue-50 hover:border-blue-300 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="font-medium text-gray-900 truncate group-hover:text-blue-700 transition-colors">
                          {report.file_name}
                        </p>
                        {report.file_type === 'manual' && (
                          <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                            手動
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 mt-1.5">
                        <span className="flex items-center text-xs text-gray-500">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDateTime(report.uploaded_at || report.upload_time || report.created_at)}
                        </span>
                        {report.status && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            report.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : report.status === 'failed'
                              ? 'bg-red-100 text-red-700'
                              : report.status === 'analyzing'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {report.status === 'completed' ? '已分析' : report.status === 'failed' ? '失敗' : report.status === 'analyzing' ? '分析中' : '待分析'}
                          </span>
                        )}
                        {report.status === 'completed' && report.amount_with_tax > 0 && (
                          <span className="text-xs text-gray-400">
                            扣款 ${Number(report.amount_with_tax).toLocaleString()}
                          </span>
                        )}
                      </div>

                      {report.status === 'failed' && (
                        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setExpandedErrorId(
                                expandedErrorId === report.id ? null : report.id
                              )}
                              className="text-xs text-red-600 hover:text-red-800 underline"
                            >
                              {expandedErrorId === report.id ? '收起原因' : '查看原因'}
                            </button>
                            {canEdit && (
                              <button
                                type="button"
                                onClick={(e) => handleRetryReport(e, report)}
                                disabled={retrying === report.id}
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                              >
                                {retrying === report.id ? (
                                  <Loader className="w-3 h-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3 h-3" />
                                )}
                                重新分析
                              </button>
                            )}
                          </div>
                          {expandedErrorId === report.id && (() => {
                            const detail = getFailureDetail(report) || {};
                            return (
                              <div className="mt-2 rounded-lg bg-red-50 border border-red-100 p-2 text-xs text-red-800 space-y-1">
                                <p>{detail.message || '報表分析失敗'}</p>
                                {detail.missing_fields?.length > 0 && (
                                  <p>缺少欄位：{detail.missing_fields.join('、')}</p>
                                )}
                                {detail.candidates?.length > 0 && (
                                  <p>
                                    候選欄位：{detail.candidates.map((candidate) =>
                                      `${candidate.column} (${candidate.score}分)`
                                    ).join('、')}
                                  </p>
                                )}
                                {detail.detected_headers?.length > 0 && (
                                  <p>偵測到的欄名：{detail.detected_headers.join('、')}</p>
                                )}
                                {Object.entries(detail.suggestions || {}).map(([field, suggestions]) => (
                                  suggestions?.length > 0 ? (
                                    <p key={field}>
                                      {field} 建議：{suggestions.map((suggestion) =>
                                        `${suggestion.column} (${Math.round((suggestion.similarity || 0) * 100)}%)`
                                      ).join('、')}
                                    </p>
                                  ) : null
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* 確認刪除警告 */}
                      {confirmDeleteId === report.id && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span>
                            {report.status === 'completed'
                              ? '刪除後將退還扣除的費用至預算，再次點擊刪除確認'
                              : '確定要刪除此報表？再次點擊刪除確認'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 shrink-0">
                      {/* 下載按鈕 */}
                      <button
                        onClick={(e) => handleDownloadOriginal(e, report)}
                        disabled={downloading === report.id}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="下載原始報表"
                      >
                        {downloading === report.id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                      {/* 單一刪除按鈕 */}
                      {canEdit && (
                        <button
                          onClick={(e) => handleDeleteReport(e, report)}
                          disabled={deleting === report.id}
                          className={`p-2 rounded-lg transition-colors ${
                            confirmDeleteId === report.id
                              ? 'text-red-600 bg-red-100 hover:bg-red-200'
                              : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                          }`}
                          title={confirmDeleteId === report.id ? '確認刪除' : '刪除報表'}
                        >
                          {deleting === report.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部提示 */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-gray-400 text-center">
            點擊報表查看費用明細分析 · 點擊 <Download className="w-3 h-3 inline" /> 下載原始上傳檔案
          </p>
        </div>
      </div>
    </div>
  );
}
